import { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const PASSWORD_POLICY =
  'Mot de passe : 10 caractères minimum, avec au moins une majuscule, une minuscule et un chiffre.';

function checkPolicy(pwd: string): string | null {
  if (pwd.length < 10) return PASSWORD_POLICY;
  if (!/[A-Z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[a-z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[0-9]/.test(pwd)) return PASSWORD_POLICY;
  return null;
}

/**
 * Page affichée après login si user.must_reset_password === 1.
 * Bloque l'accès au reste de l'app tant que le mdp n'a pas été changé.
 */
export default function ForceResetPassword() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirmPwd) {
      setError('Les deux nouveaux mots de passe ne correspondent pas.');
      return;
    }
    const polErr = checkPolicy(next);
    if (polErr) {
      setError(polErr);
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      await refresh();
      nav('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={onSubmit}>
        <h1>Définir un nouveau mot de passe</h1>
        <p className="sub">
          Bonjour {user.name}. Pour des raisons de sécurité, tu dois changer le mot de passe
          temporaire qui t'a été attribué avant de continuer.
        </p>

        {error && <div className="error-msg">{error}</div>}

        <div className="field">
          <label>Mot de passe temporaire actuel</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="field">
          <label>Nouveau mot de passe</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
            minLength={10}
          />
          <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 4 }}>
            {PASSWORD_POLICY}
          </div>
        </div>

        <div className="field">
          <label>Confirmer le nouveau mot de passe</label>
          <input
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            autoComplete="new-password"
            required
            minLength={10}
          />
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Enregistrement…' : 'Définir et continuer'}
        </button>
      </form>
    </div>
  );
}
